/*
 * Newsletter details modal.
 *
 * The inline newsletter field collects an email address only — it is not a form and
 * cannot submit. Pressing subscribe validates the email, then opens a modal holding the
 * real form, which also asks for first name, last name and job title (and reCAPTCHA when
 * the theme setting is on).
 *
 * Loaded once per page: the snippet may render several times, so everything is delegated
 * off the document and guarded by the flag below.
 */
(function () {
  if (window.__nlmNewsletterModal) return;
  window.__nlmNewsletterModal = true;

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  var activeModal = null;
  var lastTrigger = null;
  var recaptchaQueued = [];
  var recaptchaLoading = false;

  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function all(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  /* ---------- reCAPTCHA (v2 checkbox, rendered lazily on first open) ---------- */

  function grecaptchaReady() {
    return !!(window.grecaptcha && typeof window.grecaptcha.execute === 'function');
  }

  /*
   * reCAPTCHA v3 — the same site key the NetSuite contact form uses. There is no widget to
   * tick; a token is fetched on submit. api.js may already be on the page (the contact form
   * loads it, and Shopify injects its own for {% form 'customer' %}), so only inject it if
   * nothing has, and poll rather than relying on an onload= callback that never fires when
   * the API is already present.
   */
  function loadRecaptcha(sitekey) {
    if (recaptchaLoading || grecaptchaReady()) return;
    if (document.querySelector('script[src*="recaptcha/api.js"]')) return;
    recaptchaLoading = true;
    var s = document.createElement('script');
    s.src = 'https://www.google.com/recaptcha/api.js?render=' + encodeURIComponent(sitekey);
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
  }

  function initRecaptcha(modal) {
    var field = $('[data-nlm-token]', modal);
    if (field) loadRecaptcha(field.dataset.sitekey);
  }

  // Resolves with a token, or with null if reCAPTCHA is unavailable — a broken or blocked
  // captcha must not dead-end a genuine subscriber.
  function getRecaptchaToken(field) {
    return new Promise(function (resolve) {
      var settled = false;
      var done = function (value) {
        if (settled) return;
        settled = true;
        resolve(value);
      };

      loadRecaptcha(field.dataset.sitekey);
      setTimeout(function () {
        if (!settled) console.warn('[newsletter] reCAPTCHA timed out; submitting without a token.');
        done(null);
      }, 8000);

      var waited = 0;
      var timer = setInterval(function () {
        if (grecaptchaReady()) {
          clearInterval(timer);
          window.grecaptcha.ready(function () {
            window.grecaptcha
              .execute(field.dataset.sitekey, { action: 'newsletter_subscribe' })
              .then(done)
              .catch(function (e) {
                console.warn('[newsletter] reCAPTCHA execute failed:', e);
                done(null);
              });
          });
          return;
        }
        waited += 200;
        if (waited >= 8000) clearInterval(timer);
      }, 200);
    });
  }

  /* ---------- Portal ---------- */

  /*
   * Sections such as the parallax newsletter sit inside a transformed ancestor, which
   * becomes the containing block for position:fixed and clips the modal inside the
   * section. Moving each modal to <body> keeps it a true full-viewport overlay.
   */
  function portal(modal) {
    if (!modal || modal.parentNode === document.body) return modal;
    var stale = document.querySelector('body > [data-nlm-modal]#' + CSS.escape(modal.id));
    if (stale && stale !== modal) stale.remove();
    document.body.appendChild(modal);
    return modal;
  }

  function portalAll() {
    all('[data-nlm-modal]').forEach(portal);
  }

  /* ---------- Open / close ---------- */

  function openModal(modal, trigger) {
    if (!modal || activeModal === modal) return;
    portal(modal);
    lastTrigger = trigger || null;
    activeModal = modal;
    modal.hidden = false;
    document.documentElement.classList.add('nlm-open');
    initRecaptcha(modal);
    var first = $('[data-nlm-form] input:not([type="hidden"])', modal);
    if (first) first.focus();
  }

  function closeModal(modal) {
    modal = modal || activeModal;
    if (!modal) return;
    modal.hidden = true;
    if (modal === activeModal) activeModal = null;
    if (!document.querySelector('[data-nlm-modal]:not([hidden])')) {
      document.documentElement.classList.remove('nlm-open');
    }
    if (lastTrigger) {
      lastTrigger.focus();
      lastTrigger = null;
    }
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-nlm-open]');
    if (opener) {
      e.preventDefault();
      var teaser = opener.closest('[data-nlm-teaser]');
      var modal = document.getElementById(teaser.dataset.nlmTarget);
      var teaserEmail = $('[data-nlm-teaser-email]', teaser);

      // Email is mandatory before the modal opens — nothing is submitted at this point.
      teaserEmail.setAttribute('required', 'required');
      if (!teaserEmail.checkValidity()) {
        teaserEmail.reportValidity();
        return;
      }

      var modalEmail = $('[data-nlm-email]', modal);
      if (modalEmail) modalEmail.value = teaserEmail.value.trim();
      openModal(modal, opener);
      return;
    }

    if (e.target.closest('[data-nlm-close]')) {
      e.preventDefault();
      closeModal(e.target.closest('[data-nlm-modal]'));
    }
  });

  // Enter in the inline email field opens the modal instead of doing nothing.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && activeModal) {
      closeModal();
      return;
    }

    if (e.key === 'Enter' && e.target.matches('[data-nlm-teaser-email]')) {
      e.preventDefault();
      var btn = $('[data-nlm-open]', e.target.closest('[data-nlm-teaser]'));
      if (btn) btn.click();
      return;
    }

    if (e.key === 'Tab' && activeModal) {
      var items = all(FOCUSABLE, activeModal).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  /* ---------- Submit ---------- */

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target.closest('[data-nlm-form]');
      if (!form) return;

      // Shopify's customer form has no job title field, so ride it in on the tags.
      if (form.dataset.nlmPlatform === 'shopify') {
        var tags = $('[data-nlm-tags]', form);
        var job = $('[data-nlm-job]', form);
        if (tags) {
          var value = job && job.value.trim() ? job.value.trim().replace(/,/g, ' ') : '';
          tags.value = value ? 'newsletter, Job title: ' + value : 'newsletter';
        }
      }

      var field = $('[data-nlm-token]', form);

      // Second pass: token is in, let the submit run (theme ajax handlers included).
      if (!field || form.dataset.nlmTokenReady === '1') {
        delete form.dataset.nlmTokenReady;
        return;
      }

      // First pass: hold the submit while v3 scores the visitor.
      e.preventDefault();
      e.stopPropagation();
      setBusy(form, true);
      getRecaptchaToken(field).then(function (token) {
        field.value = token || '';
        form.dataset.nlmTokenReady = '1';
        setBusy(form, false);
        if (form.requestSubmit) form.requestSubmit();
        else form.submit();
      });
    },
    true
  );

  function setBusy(form, busy) {
    var btn = $('[type="submit"]', form);
    if (!btn) return;
    btn.disabled = busy;
    var spinner = $('.t4s-loading__spinner', btn);
    var text = $('.t4s-newsletter__text', btn);
    if (spinner) spinner.classList.toggle('t4s-dn', !busy);
    if (text) text.classList.toggle('t4s-dn', busy);
  }

  /* ---------- Post-submit state (Shopify reloads the page) ---------- */

  function syncResponses() {
    portalAll();
    all('[data-nlm-modal]').forEach(function (modal) {
      var teaser = document.querySelector('[data-nlm-target="' + modal.id + '"]');
      var success = $('[data-nlm-success]', modal);
      var errors = $('[data-nlm-errors]', modal);

      if (success && teaser) {
        // Show the confirmation inline; there is no reason to reopen the modal.
        $('[data-nlm-teaser-response]', teaser).appendChild(success);
        var teaserEmail = $('[data-nlm-teaser-email]', teaser);
        if (teaserEmail) teaserEmail.value = '';
        return;
      }

      if (errors && errors.textContent.trim()) {
        openModal(modal, null);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncResponses);
  } else {
    syncResponses();
  }

  // Theme editor re-renders sections, which puts a fresh modal back inside the section.
  document.addEventListener('shopify:section:load', portalAll);
})();
